import { UzButton } from '@utazzo/ui';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import React from 'react';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

export interface DataGridPaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: ReadonlyArray<number>;
}

/**
 * DataGrid footer: range count (left), page-size selector + page navigation
 * (right). Lives inside the grid card with a top border. Icon buttons are
 * ghost and disable at the bounds.
 */
export const DataGridPagination = ({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: DataGridPaginationProps): React.JSX.Element => {
  const totalPages = Math.max(Math.ceil(totalItems / pageSize), 1);
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const start = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, totalItems);

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3.5 gap-y-2 border-t border-[var(--ds-border)] px-3.5 py-2">
      <div className="text-[12px] text-[var(--fg-muted)] tabular-nums">
        {totalItems === 0 ? 'No results' : `${start}-${end} of ${totalItems}`}
      </div>
      <div className="flex items-center gap-3.5">
        <span className="inline-flex items-center gap-1.5 text-[12px]">
          <span className="text-[var(--fg-faint)]">Rows</span>
          <select
            aria-label="Rows per page"
            className="h-[26px] rounded-md border border-[var(--ds-border)] bg-[var(--bg)] pr-2 pl-2 text-[12px] text-[var(--fg)]"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </span>
        <span className="inline-flex items-center gap-0.5">
          <UzButton
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={safePage <= 1}
            onClick={() => onPageChange(1)}
            aria-label="First page"
          >
            <ChevronsLeft className="h-3.5 w-3.5" />
          </UzButton>
          <UzButton
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={safePage <= 1}
            onClick={() => onPageChange(safePage - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </UzButton>
          <span className="mx-1.5 text-[12px] text-[var(--fg-muted)] tabular-nums">
            Page {safePage} of {totalPages}
          </span>
          <UzButton
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={safePage >= totalPages}
            onClick={() => onPageChange(safePage + 1)}
            aria-label="Next page"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </UzButton>
          <UzButton
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={safePage >= totalPages}
            onClick={() => onPageChange(totalPages)}
            aria-label="Last page"
          >
            <ChevronsRight className="h-3.5 w-3.5" />
          </UzButton>
        </span>
      </div>
    </div>
  );
};
